import { parseApiDate } from '../../../shared/utils/format';

export const RECOMMENDATION_RUN_STATUS = Object.freeze({
  NO_RUN: 'NO_RUN',
  READY: 'READY',
  STALE: 'STALE',
  UPDATING: 'UPDATING',
});

const valueFrom = (recommendation, keys) => keys
  .map(key => key.split('.').reduce((value, part) => value?.[part], recommendation))
  .find(value => value !== null && value !== undefined && value !== '');

const generatedAtFor = recommendation => valueFrom(recommendation, [
  'recommendationRun.generatedAt',
  'recommendation_run.generated_at',
  'run.generatedAt',
  'run.generated_at',
  'generatedAt',
  'generated_at',
  'analyzedAt',
  'analysedAt',
]);

const explicitRunStatusFor = recommendation => String(valueFrom(recommendation, [
  'recommendationRun.status',
  'recommendation_run.status',
  'run.status',
  'runStatus',
  'run_status',
]) || '').toUpperCase();

const inventoryVersionChanged = (recommendation) => {
  const runVersion = valueFrom(recommendation, [
    'recommendationRun.inventoryVersion',
    'recommendation_run.inventory_version',
    'run.inventoryVersion',
    'run.inventory_version',
    'runInventoryVersion',
    'run_inventory_version',
  ]);
  const currentVersion = valueFrom(recommendation, [
    'sourceInventoryVersion',
    'source_inventory_version',
    'currentInventoryVersion',
    'current_inventory_version',
    'inventoryVersion',
    'inventory_version',
    'inventory.version',
  ]);
  return runVersion !== undefined
    && currentVersion !== undefined
    && String(runVersion) !== String(currentVersion);
};

export const fallbackRecommendationGeneratedAt = fiscalYear => (
  `${Number(fiscalYear)}-08-20T09:00:00+09:00`
);

/** Resolve one FY-wide run before Scope/search controls filter its display projection. */
export function resolveRecommendationRun(recommendations, fiscalYear) {
  if (!recommendations?.length) {
    return {
      candidateCount: 0,
      generatedAt: null,
      status: RECOMMENDATION_RUN_STATUS.NO_RUN,
    };
  }

  const generatedTimes = recommendations
    .map(generatedAtFor)
    .map(value => ({ value, time: Date.parse(value) }))
    .filter(item => Number.isFinite(item.time))
    .sort((left, right) => right.time - left.time);
  const stale = recommendations.some(recommendation => (
    recommendation.isStale === true
    || recommendation.stale === true
    || explicitRunStatusFor(recommendation) === RECOMMENDATION_RUN_STATUS.STALE
    || inventoryVersionChanged(recommendation)
  ));

  return {
    candidateCount: recommendations.length,
    generatedAt: generatedTimes[0]?.value || fallbackRecommendationGeneratedAt(fiscalYear),
    status: stale ? RECOMMENDATION_RUN_STATUS.STALE : RECOMMENDATION_RUN_STATUS.READY,
  };
}

export function formatRecommendationRunDate(value, locale) {
  const date = parseApiDate(value);
  if (!Number.isFinite(date.getTime())) return '';
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'Asia/Tokyo',
  }).format(date);
}
